// Programmatic lens renderer (Lenses piece 3, post-pivot).
//
// This is the daemon-side render executor. It runs when a scoped client sends
// `lenses_render_request`. There is no LLM in the loop — a lens render is
// deterministic:
//
//   1. Read `<cwd>/.appostle/brand/prose/photography/<lensSlug>.md`.
//   2. Parse YAML frontmatter; fuse the Mystic prompt from H2 sections in a
//      fixed order (Visual Identity → Subject & Composition → Lens & Camera →
//      Lighting & Atmosphere → Color & Palette → Materials & Texture). Prepend
//      the sanitized client subject; append `Avoid: ` + Forbidden bullets.
//      `{{key}}` placeholders are substituted from validated variables +
//      internal knobs.
//   3. POST to `api.freepik.com/v1/ai/mystic` with `x-freepik-api-key: FREEPIK_API_KEY`.
//   4. Poll every 5s (max 60 attempts) until COMPLETED or FAILED.
//   5. Download the finished image, save under
//      `<cwd>/_generated-images/<lensSlug>/<imageSlug>.webp`.
//   6. Emit progress + a data-URI back to the client.
//
// Errors are mapped to structured codes so the client can render friendly
// copy. No LLM, no agent spawning — this is a plain deterministic pipeline.
//
// The FREEPIK_API_KEY env var is read once (by session.ts) and threaded into
// this function. When absent → `magnific_key_missing` error, no throw.

import { readFile, mkdir, writeFile, appendFile } from "node:fs/promises";
import { createHash, randomBytes } from "node:crypto";
import { join } from "path";
import { parse as parseYaml } from "yaml";
import type pino from "pino";
import { sanitizeSubject } from "./client-scope.js";

const PROSE_RELATIVE_DIR = join(".appostle", "brand", "prose", "photography");
const MYSTIC_ENDPOINT = "https://api.freepik.com/v1/ai/mystic";

export type RenderStage = "posted" | "polling" | "downloading" | "saved";

export interface RunLensRenderArgs {
  cwd: string;
  lensSlug: string;
  subject: string;
  variables: Record<string, string>;
  clientUserId: string;
  appostleHome: string;
  magnificApiKey: string | null;
  logger: pino.Logger;
  onProgress: (stage: RenderStage, detail?: string) => void;
}

export interface RunLensRenderResult {
  ok: boolean;
  imageUrl?: string;
  imageSlug?: string;
  error?: string;
  errorCode?: string;
}

// ---- Prompt fusion ---------------------------------------------------------

const SECTION_ORDER = [
  "Visual Identity",
  "Subject & Composition",
  "Lens & Camera",
  "Lighting & Atmosphere",
  "Color & Palette",
  "Materials & Texture",
] as const;

function extractFrontmatter(source: string): { fm: Record<string, unknown>; body: string } {
  if (!source.startsWith("---")) return { fm: {}, body: source };
  const end = source.indexOf("\n---", 3);
  if (end === -1) return { fm: {}, body: source };
  const yamlBlock = source.slice(source.indexOf("\n") + 1, end);
  const body = source.slice(end + 4);
  try {
    const fm = (parseYaml(yamlBlock) as Record<string, unknown>) ?? {};
    return { fm, body };
  } catch {
    return { fm: {}, body };
  }
}

/**
 * Extracts the paragraph/bullets under an `## <name>` heading, up to the next
 * H2 heading or the end of the doc. Case-insensitive on the heading text.
 */
function extractH2Section(body: string, name: string): string {
  const lines = body.split("\n");
  const marker = `## ${name.toLowerCase()}`;
  let inSection = false;
  const collected: string[] = [];
  for (const line of lines) {
    const lower = line.toLowerCase().trim();
    if (lower.startsWith("## ")) {
      if (inSection) break;
      if (lower === marker) inSection = true;
      continue;
    }
    if (inSection) collected.push(line);
  }
  return collected.join("\n").trim();
}

function extractForbidden(body: string): string[] {
  const section = extractH2Section(body, "Forbidden");
  if (!section) return [];
  return section
    .split("\n")
    .map((l) => l.replace(/^\s*[-*]\s*/, "").trim())
    .filter((l) => l.length > 0);
}

function substitutePlaceholders(input: string, values: Record<string, string>): string {
  return input.replace(/\{\{([a-zA-Z0-9_-]+)\}\}/g, (_, key: string) => {
    const v = values[key];
    return typeof v === "string" ? v : "";
  });
}

function fusePrompt(args: {
  subject: string;
  body: string;
  values: Record<string, string>;
}): string {
  const parts: string[] = [args.subject.trim()];
  for (const name of SECTION_ORDER) {
    const section = extractH2Section(args.body, name);
    if (section) parts.push(substitutePlaceholders(section, args.values));
  }
  const forbidden = extractForbidden(args.body);
  if (forbidden.length > 0) {
    parts.push(`Avoid: ${forbidden.join("; ")}`);
  }
  return parts.join("\n\n");
}

// ---- Magnific mapping ------------------------------------------------------

function resolutionFor(quality: string | undefined): "1k" | "2k" {
  return quality === "final" ? "2k" : "1k";
}

function aspectRatioFor(
  format: string | undefined,
): "square_1_1" | "social_story_9_16" | "widescreen_16_9" | "portrait_3_4" {
  switch (format) {
    case "story":
      return "social_story_9_16";
    case "landscape":
    case "banner":
      return "widescreen_16_9";
    case "portrait":
      return "portrait_3_4";
    default:
      return "square_1_1";
  }
}

function kebab(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

// ---- Renderer --------------------------------------------------------------

export async function runLensRender(args: RunLensRenderArgs): Promise<RunLensRenderResult> {
  const {
    cwd,
    lensSlug,
    subject,
    variables,
    clientUserId,
    appostleHome,
    magnificApiKey,
    logger,
    onProgress,
  } = args;

  const log = logger.child({ component: "lens-renderer", lensSlug });

  if (!magnificApiKey) {
    await appendRenderLog(appostleHome, clientUserId, {
      ts: new Date().toISOString(),
      lensSlug,
      cwd,
      status: "error",
      errorCode: "magnific_key_missing",
    });
    return {
      ok: false,
      error: "FREEPIK_API_KEY not set on daemon",
      errorCode: "magnific_key_missing",
    };
  }

  // 1) Sanitize subject.
  const sanitized = sanitizeSubject(subject);
  if (!sanitized.ok) {
    return {
      ok: false,
      error: sanitized.error ?? "invalid subject",
      errorCode: "client_subject_invalid",
    };
  }

  // 2) Read lens prose.
  const lensPath = join(cwd, PROSE_RELATIVE_DIR, `${lensSlug}.md`);
  let source: string;
  try {
    source = await readFile(lensPath, "utf8");
  } catch (err) {
    log.warn({ err, lensPath }, "lens prose not found");
    return { ok: false, error: "lens file not found", errorCode: "lens_not_found" };
  }

  const { fm, body } = extractFrontmatter(source);
  const lensBlock = (fm.lens as Record<string, unknown> | undefined) ?? {};
  const internal = (lensBlock.internal as Record<string, unknown> | undefined) ?? {};

  // Merge validated client variables with any frontmatter internal knobs
  // (internal wins for keys the client shouldn't influence, but for prompt
  // substitution both sources feed the placeholder map).
  const values: Record<string, string> = { ...variables };
  for (const [k, v] of Object.entries(internal)) {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      values[k] = String(v);
    }
  }
  values.subject = sanitized.value ?? "";

  const prompt = fusePrompt({ subject: sanitized.value ?? "", body, values });

  // 3) Map to Mystic body.
  const quality = (variables.quality ?? "draft") as string;
  const format = (variables.format ?? "square") as string;
  const resolution = resolutionFor(quality);
  const aspect_ratio = aspectRatioFor(format);
  const model = (internal.model as string | undefined) ?? "realism";
  const creative_detailing =
    typeof internal.creative_detailing === "number" ? (internal.creative_detailing as number) : 33;

  const mysticBody = {
    prompt,
    resolution,
    aspect_ratio,
    model,
    creative_detailing,
    fixed_generation: false,
  };

  onProgress("posted");
  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "posted",
    subject: sanitized.value,
  });

  // 4) POST to Magnific.
  let taskId: string;
  try {
    const res = await fetch(MYSTIC_ENDPOINT, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-freepik-api-key": magnificApiKey,
      },
      body: JSON.stringify(mysticBody),
    });
    if (res.status === 401) {
      return errorResult("magnific_auth", "Magnific API key rejected");
    }
    if (res.status === 402) {
      return errorResult("magnific_credits", "Magnific credit balance exhausted");
    }
    if (res.status === 429) {
      // Retry once after 30s.
      await sleep(30_000);
      const retry = await fetch(MYSTIC_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", "x-freepik-api-key": magnificApiKey },
        body: JSON.stringify(mysticBody),
      });
      if (!retry.ok)
        return errorResult("magnific_rate_limited", `Mystic POST 429 (retry: ${retry.status})`);
      const retryJson = (await retry.json()) as { data?: { id?: string } };
      taskId = retryJson.data?.id ?? "";
    } else if (!res.ok) {
      const detail = await res.text().catch(() => "");
      return errorResult("magnific_task_failed", `Mystic POST ${res.status}: ${detail}`);
    } else {
      const json = (await res.json()) as { data?: { id?: string } };
      taskId = json.data?.id ?? "";
    }
    if (!taskId) return errorResult("magnific_task_failed", "no task_id in POST response");
  } catch (err) {
    log.error({ err }, "Mystic POST failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "network error");
  }

  // 5) Poll.
  onProgress("polling", `task ${taskId}`);
  const pollUrl = `${MYSTIC_ENDPOINT}/${taskId}`;
  let generatedUrl: string | null = null;
  for (let attempt = 0; attempt < 60; attempt++) {
    await sleep(5000);
    try {
      const res = await fetch(pollUrl, {
        headers: { "x-freepik-api-key": magnificApiKey },
      });
      if (!res.ok) {
        // transient — keep polling
        continue;
      }
      const json = (await res.json()) as {
        data?: { status?: string; generated?: string[] };
      };
      const status = json.data?.status;
      if (status === "COMPLETED") {
        generatedUrl = json.data?.generated?.[0] ?? null;
        break;
      }
      if (status === "FAILED") {
        return errorResult("magnific_task_failed", "Mystic task reported FAILED");
      }
    } catch {
      // network flake, try again
    }
  }
  if (!generatedUrl) {
    return errorResult("magnific_timeout", "Mystic poll exhausted without COMPLETED");
  }

  // 6) Download + save.
  onProgress("downloading");
  const imageSlug = `${lensSlug}-${kebab(sanitized.value ?? "subject")}-${randomBytes(4).toString(
    "hex",
  )}`;
  const outDir = join(cwd, "_generated-images", lensSlug);
  const outPath = join(outDir, `${imageSlug}.webp`);
  let bytes: Buffer;
  try {
    const dl = await fetch(generatedUrl);
    if (!dl.ok) return errorResult("magnific_task_failed", `download ${dl.status}`);
    const arr = new Uint8Array(await dl.arrayBuffer());
    bytes = Buffer.from(arr);
    await mkdir(outDir, { recursive: true });
    await writeFile(outPath, bytes);
  } catch (err) {
    log.error({ err }, "image download failed");
    return errorResult("magnific_task_failed", (err as Error)?.message ?? "download failed");
  }

  onProgress("saved", outPath);
  await appendRenderLog(appostleHome, clientUserId, {
    ts: new Date().toISOString(),
    lensSlug,
    cwd,
    status: "saved",
    imageSlug,
    outPath,
  });

  // Return a data-URI so the client renders without an HTTP round-trip.
  // (Pragmatic fallback per plan — issuing a download-token URL would need
  // route wiring beyond the scope of this piece.)
  const dataUri = `data:image/webp;base64,${bytes.toString("base64")}`;
  return { ok: true, imageUrl: dataUri, imageSlug };
}

// ---- Helpers ---------------------------------------------------------------

function errorResult(errorCode: string, error: string): RunLensRenderResult {
  return { ok: false, error, errorCode };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function appendRenderLog(
  appostleHome: string,
  clientUserId: string,
  entry: Record<string, unknown>,
): Promise<void> {
  const dir = join(appostleHome, "lenses");
  const file = join(dir, `renders-${sha8(clientUserId)}.jsonl`);
  try {
    await mkdir(dir, { recursive: true });
    await appendFile(file, `${JSON.stringify(entry)}\n`);
  } catch {
    // best-effort log — never throw from here
  }
}

function sha8(input: string): string {
  return createHash("sha256").update(input, "utf8").digest("hex").slice(0, 12);
}
